import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Telescope, Wrench } from "lucide-react";

const PILLARS = [
    {
        icon: Compass,
        label: "Mission",
        title: "Remove the cable — reliably.",
        body:
            "We exist to replace manual plug-in charging with automatic, contactless energy transfer that works day in and day out in real commercial and industrial environments.",
    },
    {
        icon: Telescope,
        label: "Vision",
        title: "Charging that simply happens.",
        body:
            "A world where commercial vehicles, industrial equipment and smart infrastructure receive energy automatically — safely, intelligently, and without human intervention.",
    },
    {
        icon: Wrench,
        label: "How we build",
        title: "Engineering-first, transparent by default.",
        body:
            "We are power-electronics, electromagnetics and systems engineers. We build for reliability and daily operation — and we're transparent about what our platform delivers today versus what's on the roadmap.",
    },
];

const PRINCIPLES = [
    {
        n: "01",
        t: "Reliable by design",
        d: "Every product is engineered for continuous, dependable daily commercial operation.",
    },
    {
        n: "02",
        t: "Automation-first",
        d: "We optimize for use cases where charging must happen without human intervention.",
    },
    {
        n: "03",
        t: "Integrate, don't disrupt",
        d: "Our systems fit into existing fleets, sites and infrastructure — modular, standards-aligned, retrofit-friendly.",
    },
    {
        n: "04",
        t: "Transparent about the roadmap",
        d: "We're honest about today's capabilities and clear about where the platform is going next.",
    },
];

const About = () => {
    return (
        <main data-testid="about-page" className="bg-white text-[#050B14]">
            {/* HERO */}
            <section className="relative bg-[#050B14] text-white pt-32 pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#0055FF]/15 blur-3xl" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-3xl">
                        <div className="kicker text-[#00E5FF] mb-6 flex items-center gap-3">
                            <span className="w-8 h-px bg-[#00E5FF]" /> About ELWIS
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Engineering wireless charging for <span className="text-gradient-cyan">real-world operations.</span>
                        </h1>
                        <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-2xl">
                            ELWIS — Electric Wireless Solutions — develops intelligent
                            wireless charging systems for commercial vehicles,
                            industrial equipment and smart infrastructure.
                        </p>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {PILLARS.map((p) => (
                            <div key={p.label} className="bg-white p-10">
                                <p.icon className="w-7 h-7 text-[#0055FF]" />
                                <div className="kicker text-slate-400 mt-6">{p.label}</div>
                                <h3 className="mt-3 font-display font-bold text-2xl lg:text-3xl tracking-tight text-[#0A0F1C] leading-tight">
                                    {p.title}
                                </h3>
                                <p className="mt-5 text-slate-600 leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRINCIPLES */}
            <section className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 mb-14">
                        <div className="lg:col-span-5">
                            <div className="kicker text-[#0055FF] mb-5">Engineering principles</div>
                            <h2 className="font-display font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0F1C]">
                                Four principles guide every system we ship.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="text-slate-600 leading-relaxed">
                                These are the engineering filters every ELWIS product
                                passes through — from the first concept sketch to the
                                final field deployment.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                        {PRINCIPLES.map((p) => (
                            <div key={p.n} className="bg-white p-10">
                                <div className="font-mono text-sm text-[#0055FF] mb-4">{p.n}</div>
                                <h3 className="font-display font-bold text-xl text-[#0A0F1C]">{p.t}</h3>
                                <p className="mt-3 text-slate-600 leading-relaxed">{p.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA — DARK */}
            <section className="py-24 lg:py-32 bg-[#050B14] text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="grid lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-8">
                            <div className="kicker text-[#00E5FF] mb-5">Build with us</div>
                            <h2 className="font-display font-bold text-4xl lg:text-6xl tracking-tight leading-[1.05]">
                                Let's build the next generation of <span className="text-gradient-cyan">wireless charging infrastructure</span> together.
                            </h2>
                        </div>
                        <div className="lg:col-span-4">
                            <Link
                                to="/contact"
                                data-testid="about-cta-contact"
                                className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#050B14] px-6 py-4 rounded-md text-base font-semibold hover:bg-white transition-colors"
                            >
                                Start a conversation
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default About;
